import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as PR from "../../../../prime-react/index";
import "./PlayerStatsHistory.scss";
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../../../../layouts/Layout';
import {  getPlayerAnalytics, getPlayerAnalyticsBets, getSearchOptions } from '../../../../services/api';
import { useLocation, useParams } from 'react-router-dom';
import { useToast } from '../../../../context/ToastContext';
import moment from 'moment';
import { currencyFormat, formatShortDate, numberFormat, pagecount } from '../../../../utils/reuse';
import SearchAndTotalSummary from '../search-total-summary/SearchAndTotalSummary';
import { RAW_FILTER_DATA } from '../../../../shared/common';
import BetList from '../bet-list/BetList';
import { storePlayerFilters } from '../../../../store/filter.slice';
import { isEmpty } from 'lodash';
import { filterOptionsActions } from "../../../../store/FiltersOptions";

const PlayerStatsHistory = () => {
    const { showToast } = useToast();
    const { state } = useLocation();
    const [betList, setBetList] = useState([]);
    const sessionData = useSelector((state) => state.auth.sessionData);
    const headers = useMemo(() => {
        return { sessionid: sessionData.sessionId };
    }, [sessionData.sessionId]);
    const params = useParams();
    const groupLoadedRef = useRef(false);
    const channelLoadedRef = useRef(false);
    const [playerAnalytics, setPlayerAnalytics] = useState({});
    const dispatch = useDispatch();
    const [defaultDate] = useState(() => {
        const nowUTC = new Date(new Date().toUTCString());
        const hourUTC = nowUTC.getUTCHours();
        const offsetDays = hourUTC >= 21 ? 1 : 2;
        const maxAllowedDate = new Date();
        maxAllowedDate.setUTCDate(nowUTC.getUTCDate() - offsetDays);
        return [new Date(maxAllowedDate.getFullYear(), 0, 1), maxAllowedDate];
    });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(pagecount + 1);
    const [last, setLast] = useState(true);
    const [paging, setPaging] = useState(false);
    const [sortField, setSortField] = useState('reportdate');
    const [sortOrder, setSortOrder] = useState(-1);
    const [requestType, setRequestType] = useState('');
    const alertPlatformState = useSelector(state => state.alertPlatformFilter.playerFilters);
    const storedFilterOptions = useSelector((state) => state.filterOptions);
    const [paramId, setParamId] = useState(params?.playerId ? params.playerId : alertPlatformState.playerID);
    const [searchOptions, setSearchOptions] = useState({
        group: [],
        location: [],
        channel: []
    });
    const [searchRequest, setSearchRequest] = useState(alertPlatformState);
    const [defaultGroup, setDefaultGroup] = useState('');
    const [defaultState, setDefaultState] = useState('');
    useEffect(() => {
        if (state?.date) {
            const [startDate, endDateRaw] = state.date;
            const endDate = endDateRaw ?? startDate;
            const dateRange = `${formatShortDate(startDate)} - ${formatShortDate(endDate)}`;
            setSearchRequest(prev => ({
                ...prev,
                date: { date: state.date, dateRange }
            }));
        }
    }, [state]);
 
    useEffect(() => {
       setPage(1);
       setOffset(0);
       setLast(true);
       setPaging(false);
        if (params?.playerId) {
            setParamId(params?.playerId);
            dispatch(storePlayerFilters({ ...searchRequest, playerID: params?.playerId }))
        }
    }, [dispatch, params?.playerId, searchRequest])
    const buildPayload = useCallback((request) => {
        const data = {};
        data.groupID = request.groupID ? request.groupID : defaultGroup?.id;
        data.stateID = request.stateID ? request.stateID : defaultState?.id;
        const startDate = request?.date?.date?.[0]
            ? moment(request.date.date[0]).format("YYYY-MM-DD")
            : moment(defaultDate[0]).format("YYYY-MM-DD");
        const endDate = request?.date?.date?.[1]
            ? moment(request.date.date[1]).format("YYYY-MM-DD")
            : moment(defaultDate[1]).format("YYYY-MM-DD");

        data.startDate = startDate;
        data.endDate = endDate;
        return data;
    }, [defaultDate, defaultGroup?.id, defaultState?.id]);

    const getPlayerAnalyticsHandler = useCallback((data) => {
        const id = paramId;
        const getResponse = (response) => {
            if (response.result === "SUCCESS") {
                const responseData = response.data;
                if(isEmpty(responseData?.summary)){
                    setPlayerAnalytics({})
                    showToast('warn', 'No record found');
                } else {
                    setPlayerAnalytics(responseData?.summary);
                }
            } else {
                setPlayerAnalytics({})
                showToast('warn', 'No record found');
            }
        };
        getPlayerAnalytics(id, data, headers, dispatch, getResponse);
    }, [dispatch, headers, paramId, showToast]);
    useEffect(() => {
        if (defaultGroup?.id && defaultState?.id && paramId) {
            const data = buildPayload(searchRequest);
            getPlayerAnalyticsHandler(data);
        }
    }, [buildPayload, defaultGroup?.id, defaultState?.id, getPlayerAnalyticsHandler, paramId, searchRequest]);

    const getBetList = useCallback((data) => {
        setLoading(true);
        const queryParams = {
            offset: offset,
            limit: limit,
            sort_by: sortField,
            sort_order: sortOrder === -1 ? 'desc' : 'asc',
            groupID: data.groupID,
            stateID: data.stateID,
            ...data
        };
        if(requestType){
            queryParams.bet_type = requestType
        }
        getPlayerAnalyticsBets(paramId, queryParams, headers, dispatch, (response) => {
            if (response.result === "SUCCESS") {
                const responseData = response.data?.bets;
                const result = responseData?.length > 0 ? responseData.slice(0, pagecount) : [];
                if (result.length > 0) {
                    setLast(responseData.length <= pagecount);
                    setPaging(true);
                    setBetList(result);
                } else {
                    setLast(true);
                    setPaging(false);
                    setBetList([]);
                }
            } else {
                setLast(true);
                setPaging(false);
                setBetList([]);
            }
            setLoading(false);
        });
    }, [dispatch, headers, limit, offset, paramId, requestType, sortField, sortOrder]);
    useEffect(() => {
        if (defaultGroup?.id && defaultState?.id && paramId) {
            const data = buildPayload(searchRequest);
            getBetList(data);
        }
    }, [buildPayload, defaultGroup?.id, defaultState?.id, getBetList, paramId, searchRequest]);
    const getSearchOptionsHandler = useCallback((type, groupParam = '') => {
        const getResponse = (type) => (response) => {
            if (response.result === "SUCCESS") {
                let filterOptions = response.data?.length ? response.data.map(r => ({ id: r.id, name: r.name })).filter(r => r.name?.toLowerCase() !== 'unknown') : [];
                if (type === 'channel') {
                    const channelIds = filterOptions.map(s => s.id);
                    filterOptions = RAW_FILTER_DATA.newchannels.map(f => { return channelIds.includes(f.id) ? f : RAW_FILTER_DATA.newchannels.find(c => c.id === f.id) })
                    dispatch(filterOptionsActions.storeFilterOptions({ key: type, options: filterOptions }))
                }
                if (type === 'group' && filterOptions?.length) {
                    setDefaultGroup(filterOptions[0])
                    dispatch(filterOptionsActions.storeFilterOptions({ key: type, options: filterOptions }))
                }
                if (type === 'location' && filterOptions?.length) {
                    setDefaultState(filterOptions[0])
                }
                setSearchOptions((prev) => ({
                    ...prev,
                    [type]: [...filterOptions]
                }));
            } else {
                setSearchOptions((prev) => ({
                    ...prev,
                    [type]: []
                }));
            }
        };
        getSearchOptions(type, headers, dispatch, getResponse(type), groupParam);
    }, [dispatch, headers]);

    useEffect(() => {
        if (defaultGroup?.id) {
            getSearchOptionsHandler('location', `groups[]=${defaultGroup?.id}`);
        } 
    }, [getSearchOptionsHandler, defaultGroup]);
    useEffect(() => {
        if (groupLoadedRef.current) return;
        
        if (storedFilterOptions['group']?.length === 0) {
            getSearchOptionsHandler('group');
        } else {
            setDefaultGroup(storedFilterOptions['group'][0])
            setSearchOptions((prev) => ({
                ...prev,
                'group': [...storedFilterOptions['group']]
            }));
        }
        groupLoadedRef.current = true;
    }, [getSearchOptionsHandler, storedFilterOptions]);
    useEffect(() => {
        if (channelLoadedRef.current) return;
        
        if (storedFilterOptions['channel']?.length === 0) {
            getSearchOptionsHandler('channel');
        } else {
            setSearchOptions((prev) => ({
                ...prev,
                'channel': [...storedFilterOptions['channel']]
            }));
        }
        channelLoadedRef.current = true;
    }, [getSearchOptionsHandler, storedFilterOptions]);

    const handlePageChange = (currentPage) => {
        setPaging(false);
        setPage(currentPage);
        const offsetVal = (currentPage - 1) * pagecount;
        const limitVal = pagecount + 1;
        setOffset(offsetVal);
        setLimit(limitVal);
        setLoading(true);
    };
    const sortHandler = ({ sortField, sortOrder }) => {
        setSortField(sortField);
        setSortOrder(sortOrder);
        handlePageChange(1);
    }
    const onTabChangeHandler = (type) => {
        setRequestType(type);
        setPage(1);
        setOffset(0);
        setLoading(true)
    }
    const searchRequestHandler = (data) => {
        setSearchRequest(data);
    };
    const filterHandler = (value, type) => {
        const data = searchOptions[type]?.length ? searchOptions[type] : [];
        const find = data.find(d => d.id === value);
        if (type === 'group') {
            setDefaultGroup(find)
            setDefaultState({})
            setPlayerAnalytics({})
            setBetList([])
        }
    }

    return (
        <>  
            <section className="analyst-home-section statistics-betting-section">
                <SearchAndTotalSummary searchRequestHandler={searchRequestHandler} filterHandler={filterHandler} totalData={{}} searchOptions={searchOptions} loading={false} alertPlatformState={alertPlatformState} defaultDate={defaultDate} defaultGroup={defaultGroup} defaultState={defaultState} screenType={'player'}/>
                <Layout>
                    <section className="best-list-section">
                        <div className="flex align-items-center justify-content-between">
                            <h2 className='heading-h2'>Player analytics {paramId ? ` - ${paramId}` : paramId}</h2>
                        </div>
                        {!paramId? (
                         <div className="loading-section">
                            <div className="card">
                            <div className="small-box">
                                <div className="flex align-items-center">
                                <div className="mr-2">
                                    <i className="pi pi-check-circle"></i>
                                </div>
                                <div>
                                    <PR.Skeleton className="bars"></PR.Skeleton>
                                    <PR.Skeleton className="bars custom-width"></PR.Skeleton>
                                </div>
                                </div>
                            </div>
                            <div className="big-box">
                                <div className="flex align-items-center">
                                <div className="mr-2">
                                    <i className="pi pi-check-circle"></i>
                                </div>
                                <div>
                                    <PR.Skeleton className="bars"></PR.Skeleton>
                                    <PR.Skeleton className="bars custom-width"></PR.Skeleton>
                                </div>
                                </div>
                            </div>
                            <div className="small-box">
                                <div className="flex align-items-center">
                                <div className="mr-2">
                                    <i className="pi pi-check-circle"></i>
                                </div>
                                <div>
                                    <PR.Skeleton className="bars"></PR.Skeleton>
                                    <PR.Skeleton className="bars custom-width"></PR.Skeleton>
                                </div>
                                </div>
                            </div>
                            <p>Search for any player ID to view their details..</p>
                            </div>
                        </div>
                        ) : (
                        <>
                            <section className='statistics-section'>
                                <div className='wrapper'>
                                    <div className="grid">
                                        <div className="col-12 md:col-4">
                                            <div className="box">
                                                <div>
                                                    <h4>Total Number of bets</h4>
                                                    <p>The total bets made by a player within a specified date range.</p>
                                                </div>
                                                <h2>{numberFormat(playerAnalytics?.totalBetsPlaced, 0)}</h2>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-4">
                                            <div className="box">
                                                <div>
                                                    <h4>Total Stake</h4>
                                                    <p>The total sum wagered by a player on various events or sports within a specified date range.</p>
                                                </div>
                                                <h2>{currencyFormat(playerAnalytics?.totalStake, defaultGroup?.id)}</h2>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-4">
                                            <div className="box">
                                                <div>
                                                    <h4>Average Stake</h4>
                                                    <p>The average stake amount placed by a player within a specified date range.</p>
                                                </div>
                                                <h2>{currencyFormat(playerAnalytics?.averageStake, defaultGroup?.id)}</h2>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-4">
                                            <div className="box">
                                                <div>
                                                    <h4>Total Winnings</h4>
                                                    <p>The total winnings of the player within the specified date range.</p>
                                                </div>
                                                <h2>{currencyFormat(playerAnalytics?.totalWonPayout, defaultGroup?.id)}</h2>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-4">
                                            <div className="box">
                                                <div>
                                                    <h4>Rate of Win/Loss</h4>
                                                    <p>The percentage of wins and losses achieved by the player during the specified date range.</p>
                                                </div>
                                                <h2>{numberFormat(playerAnalytics?.wonRate)}% | {numberFormat(playerAnalytics?.lostRate)}%</h2>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-4">
                                            <div className="box">
                                                <div>
                                                    <h4>Preferred Sports</h4>
                                                    <p>The three sports in which the player has consistently placed bets during the specified date range.</p>
                                                </div>
                                                <h2>{playerAnalytics?.mostBetsPlacedSports?.length
                                                    ? playerAnalytics.mostBetsPlacedSports.map(p => p?.name).join('; ') : '-'}</h2>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            <div className='stats-tab-section mt-3'>
                                <BetList betList={betList} loading={loading} page={page} paging={paging} last={last} sortField={sortField} sortOrder={sortOrder} handlePageChange={handlePageChange} sortHandler={sortHandler} onTabChange={onTabChangeHandler}  type={'player'} groupId={defaultGroup?.id} />
                            </div> 
                        </>
                        )}
                    </section>
                </Layout>
            </section>
        </>
    );
};

export default PlayerStatsHistory;
